import type { DesignSpec } from "@/types/designSpec";
import {
  createDesignSpecSnapshot,
  parseDesignSpecSnapshot,
} from "@/lib/agent/designSpecSnapshot";
import { createCreateAuthIntentId } from "@/utils/createAuthHandoffPersistence";

export const QUOTE_RESUME_ID_PARAM = "quote_resume_id";

const QUOTE_RESUME_INTENT_KEY = "create.quote.resumeAfterAuth";
const QUOTE_RESUME_INTENT_PREFIX = `${QUOTE_RESUME_INTENT_KEY}.`;
const QUOTE_RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredQuoteResumeIntent {
  intentId: string;
  resumeKey: string;
  rememberedAt: number;
  /** Exact selected quote contract, separate from the editable draft. */
  designSpecOverride?: DesignSpec;
  quoteScope?: "front_only";
}

interface StoredQuoteResumeIntents {
  intents: Record<string, StoredQuoteResumeIntent>;
}

function quoteIntentStorageKey(intentId: string): string {
  return `${QUOTE_RESUME_INTENT_PREFIX}${intentId}`;
}

function migrateLegacyQuoteResumeIntents(): void {
  const serialized = window.localStorage.getItem(QUOTE_RESUME_INTENT_KEY);
  if (!serialized) return;
  const parsed = JSON.parse(serialized) as
    | Partial<StoredQuoteResumeIntent>
    | Partial<StoredQuoteResumeIntents>;
  let intents: Record<string, StoredQuoteResumeIntent> = {};

  // An older record has no URL-bound id, so retain it only for manual cleanup;
  // a generic legacy return URL cannot auto-submit it.
  if ("resumeKey" in parsed && typeof parsed.resumeKey === "string") {
    const intentId = `legacy:${parsed.resumeKey}`;
    intents = {
      [intentId]: {
        intentId,
        resumeKey: parsed.resumeKey,
        rememberedAt: Number(parsed.rememberedAt),
      },
    };
  } else if ("intents" in parsed && parsed.intents) {
    intents = parsed.intents;
  }

  for (const intent of Object.values(intents)) {
    const storageKey = quoteIntentStorageKey(intent.intentId);
    if (!window.localStorage.getItem(storageKey)) {
      writeQuoteResumeIntent(intent);
    }
  }
  window.localStorage.removeItem(QUOTE_RESUME_INTENT_KEY);
}

function readQuoteResumeIntents(): Record<string, StoredQuoteResumeIntent> {
  migrateLegacyQuoteResumeIntents();
  const intents: Record<string, StoredQuoteResumeIntent> = {};
  for (let index = 0; index < window.localStorage.length; index++) {
    const storageKey = window.localStorage.key(index);
    if (!storageKey?.startsWith(QUOTE_RESUME_INTENT_PREFIX)) continue;
    const serialized = window.localStorage.getItem(storageKey);
    if (!serialized) continue;
    const intent = JSON.parse(serialized) as StoredQuoteResumeIntent;
    if (
      typeof intent.intentId === "string" &&
      quoteIntentStorageKey(intent.intentId) === storageKey
    ) {
      intents[intent.intentId] = intent;
    }
  }
  return intents;
}

function writeQuoteResumeIntent(intent: StoredQuoteResumeIntent): void {
  const storageKey = quoteIntentStorageKey(intent.intentId);
  const serialized = JSON.stringify(intent);
  window.localStorage.setItem(storageKey, serialized);
  if (window.localStorage.getItem(storageKey) !== serialized) {
    throw new Error("Quote resume intent was not persisted");
  }
}

function removeQuoteResumeIntent(intentId: string): void {
  const storageKey = quoteIntentStorageKey(intentId);
  window.localStorage.removeItem(storageKey);
  if (window.localStorage.getItem(storageKey) !== null) {
    throw new Error("Quote resume intent was not consumed");
  }
}

function isCurrent(intent: StoredQuoteResumeIntent): boolean {
  const ageMs = Date.now() - Number(intent.rememberedAt);
  return (
    Boolean(intent.intentId) &&
    Boolean(intent.resumeKey) &&
    Number.isFinite(ageMs) &&
    ageMs >= 0 &&
    ageMs <= QUOTE_RESUME_MAX_AGE_MS
  );
}

function readCurrentQuoteResumeIntents(): Record<
  string,
  StoredQuoteResumeIntent
> {
  const intents = readQuoteResumeIntents();
  const current = Object.fromEntries(
    Object.entries(intents).filter(([, intent]) => isCurrent(intent))
  );
  for (const intentId of Object.keys(intents)) {
    if (!current[intentId]) removeQuoteResumeIntent(intentId);
  }
  return current;
}

/** Store a one-shot quote capability and return its opaque URL id. */
export function rememberQuoteResumeAfterAuth(
  resumeKey?: string | null,
  preferredIntentId?: string | null,
  designSpecOverride?: DesignSpec,
  quoteScope?: "front_only"
): string | null {
  if (
    typeof window === "undefined" ||
    !resumeKey ||
    (quoteScope === "front_only" && !designSpecOverride)
  )
    return null;
  try {
    const intentId = preferredIntentId || createCreateAuthIntentId();
    if (!intentId) return null;
    readCurrentQuoteResumeIntents();
    writeQuoteResumeIntent({
      intentId,
      resumeKey,
      rememberedAt: Date.now(),
      ...(designSpecOverride
        ? { designSpecOverride: createDesignSpecSnapshot(designSpecOverride)! }
        : {}),
      ...(quoteScope ? { quoteScope } : {}),
    });
    return intentId;
  } catch {
    return null;
  }
}

export function hasQuoteResumeAfterAuth(
  intentId?: string | null,
  expectedResumeKey?: string | null
): boolean {
  if (typeof window === "undefined" || !intentId || !expectedResumeKey) {
    return false;
  }
  try {
    const intent = readCurrentQuoteResumeIntents()[intentId];
    return Boolean(intent && intent.resumeKey === expectedResumeKey);
  } catch {
    return false;
  }
}

export function hasPendingQuoteResumeAfterAuth(
  intentId?: string | null
): boolean {
  if (typeof window === "undefined") return false;
  try {
    const intents = readCurrentQuoteResumeIntents();
    return intentId
      ? Boolean(intents[intentId])
      : Object.keys(intents).length > 0;
  } catch {
    return false;
  }
}

export function clearQuoteResumeAfterAuth(intentId?: string | null): void {
  if (typeof window === "undefined" || !intentId) return;
  try {
    removeQuoteResumeIntent(intentId);
  } catch {
    // Best effort cleanup.
  }
}

export function clearAllQuoteResumesAfterAuth(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(QUOTE_RESUME_INTENT_KEY);
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index++) {
      const storageKey = window.localStorage.key(index);
      if (storageKey?.startsWith(QUOTE_RESUME_INTENT_PREFIX)) {
        keys.push(storageKey);
      }
    }
    keys.forEach(storageKey => window.localStorage.removeItem(storageKey));
  } catch {
    // Best effort cleanup at a real identity boundary.
  }
}

export function consumeQuoteResumeAfterAuth(
  intentId?: string | null,
  expectedResumeKey?: string | null
): boolean {
  const matches = hasQuoteResumeAfterAuth(intentId, expectedResumeKey);
  if (!matches || !intentId) return false;
  try {
    removeQuoteResumeIntent(intentId);
    return true;
  } catch {
    return false;
  }
}

export type QuoteResumeOutcome = "completed" | "retry" | "unavailable";

/** Hold one cross-tab lock until quote submission and intent consumption end. */
export async function resumeQuoteAfterAuth(
  intentId: string,
  expectedResumeKey: string,
  onResume: (
    designSpecOverride?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => boolean | Promise<boolean>
): Promise<QuoteResumeOutcome> {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !navigator.locks
  ) {
    return "unavailable";
  }

  const actionFailures: unknown[] = [];
  try {
    const outcome = await navigator.locks.request(
      `${QUOTE_RESUME_INTENT_KEY}:${intentId}`,
      async (): Promise<QuoteResumeOutcome> => {
        let intent: StoredQuoteResumeIntent | undefined;
        try {
          intent = readCurrentQuoteResumeIntents()[intentId];
          if (!intent || intent.resumeKey !== expectedResumeKey) {
            return "unavailable";
          }
          removeQuoteResumeIntent(intentId);
        } catch {
          return "unavailable";
        }

        try {
          const hasSelectedContract = "designSpecOverride" in intent;
          const designSpecOverride = hasSelectedContract
            ? parseDesignSpecSnapshot(intent.designSpecOverride)
            : undefined;
          // Never turn a corrupted saved-image selection into a quote for
          // today's pending draft.
          if (hasSelectedContract && !designSpecOverride) return "unavailable";
          if (intent.quoteScope === "front_only" && !designSpecOverride)
            return "unavailable";
          const succeeded =
            intent.quoteScope === "front_only"
              ? await onResume(designSpecOverride!, {
                  quoteScope: "front_only",
                })
              : designSpecOverride
                ? await onResume(designSpecOverride)
                : await onResume();
          if (succeeded) return "completed";
        } catch (error) {
          actionFailures.push(error);
        }
        return "retry";
      }
    );
    if (actionFailures.length > 0) {
      throw actionFailures[0];
    }
    return outcome;
  } catch {
    if (actionFailures.length > 0) throw actionFailures[0];
    return "unavailable";
  }
}
